import { useState, useEffect, useId, ChangeEvent } from "react";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ReactComponent as DriveIcon } from "../../../assets/drive.icon.svg";
import { ReactComponent as CloudIcon } from "../../../assets/cloud.icon.svg";
import { ReactComponent as CloudErrorIcon } from "../../../assets/cloud--error.icon.svg";
import { getGoogleUser, getMSUser } from "../../../api/users/endpoints-referrers";
import { addContentItemProvider } from "../../../api/admin/content-providers/endpoints-referrers";
import { Loader } from "../../../components/loader/loader.comp";
import { LoaderStep } from "../../../common/enums/loader-step.enum";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { useGooglePickerPopup } from "../../../common/utilities/google-picker/useGooglePickerPopup";
import { useOneDrivePickerPopup } from "../../../common/utilities/onedrive-picker/useOneDrivePickerPopup";
import { useOneDriveAuthPopup } from "../../../common/utilities/onedrive-auth/useOneDriveAuthPopup";
import {
    DropdownMenuType,
    SelectionFilter
} from "../../../components/menu-filter/menu-filter.comp";
import LinkWebSiteView from "../link-web-site/link-web-site";

const LinkGoogleDriveView = ({
    handleClose,
    handleSuccess
}: {
    handleClose: () => void;
    handleSuccess: () => void;
}) => {
    enum DriveType {
        google = "Google Drive",
        ms = "One Drive",
        sharepoint = "SharePoint",
        // Web scraper provider (web_scraper_provider_design.md §10): the
        // "Web site" choice swaps in the crawl form below.
        web = "Web site"
    }

    const { user } = FirebaseAuthContainer.useContainer();
    const [driveLink, setDriveLink] = useState<string>("");
    const [driveName, setDriveName] = useState<string>("");
    const [driveType, setDriveType] = useState<DriveType>();
    const [driveEmail, setDriveEmail] = useState<string>("");
    const [fileFilter, setFileFilter] = useState<string>("");
    const [liveSheetLinks, setLiveSheetLinks] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [step, setStep] = useState<LoaderStep>(LoaderStep.start);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const loadingDescription = `You can now close this window.
    Google Drive will appear when connection is complete.`;
    const completeDescription = `Files from this drive will appear soon.
    Depending on the size and quantity in may take some time.`;

    const [googleAuthToken, setGoogleAuthToken] = useState("");
    const [msAccessToken, setMSAccessToken] = useState("");
    // Base id for associating labels/error hints with their inputs (WCAG 1.3.1/3.3.2).
    const fieldId = useId();

    // Use the cross-domain Google Picker popup hook
    const { openPicker: openGooglePicker } = useGooglePickerPopup({
        onSuccess: (url, accessToken) => {
            setDriveLink(url);
            setGoogleAuthToken(accessToken);
        },
        onCancel: () => {
            setGoogleAuthToken("");
        },
        onError: error => {
            console.error("Google Picker error:", error);
            setGoogleAuthToken("");
        }
    });

    // Use the cross-domain OneDrive Picker popup hook (legacy mode)
    const { openPicker: openOneDrivePicker } = useOneDrivePickerPopup({
        useSdk8: false, // Use legacy picker
        onSuccess: (url, accessToken) => {
            setDriveLink(url);
            setMSAccessToken(accessToken);
        },
        onCancel: () => {
            setMSAccessToken("");
        },
        onError: error => {
            console.error("OneDrive Picker error:", error);
            setMSAccessToken("");
        }
    });

    // Use the cross-domain OneDrive Picker popup hook (SDK 8.0 mode)
    const { openPicker: openOneDrivePicker8 } = useOneDrivePickerPopup({
        useSdk8: true, // Use SDK 8.0 picker
        onSuccess: (url, accessToken) => {
            setDriveLink(url);
            setMSAccessToken(accessToken);
        },
        onCancel: () => {
            setMSAccessToken("");
        },
        onError: error => {
            console.error("OneDrive Picker (SDK 8.0) error:", error);
            setMSAccessToken("");
        }
    });

    // Use the cross-domain OneDrive Picker popup hook (SharePoint mode)
    const { openPicker: openSharePointPicker } = useOneDrivePickerPopup({
        mode: "sharepoint",
        onSuccess: (url, accessToken) => {
            setDriveLink(url);
            setMSAccessToken(accessToken);
        },
        onCancel: () => {
            setMSAccessToken("");
        },
        onError: error => {
            console.error("SharePoint Picker error:", error);
            setMSAccessToken("");
        }
    });

    // Auth-only popup for when user pastes a URL manually (no picker needed)
    const { openAuth: openMSAuth } = useOneDriveAuthPopup({
        onSuccess: (accessToken, email) => {
            setMSAccessToken(accessToken);
            if (email) setDriveEmail(email);
        },
        onCancel: () => {
            setMSAccessToken("");
        },
        onError: error => {
            console.error("Microsoft auth error:", error);
            setMSAccessToken("");
        }
    });

    useEffect(() => {
        if (googleAuthToken) {
            getGoogleUser(googleAuthToken).then(response => {
                setDriveEmail(response?.data?.user?.emailAddress ?? "");
            });
        } else {
            setDriveEmail("");
        }
    }, [googleAuthToken]);

    useEffect(() => {
        if (msAccessToken) {
            getMSUser(msAccessToken).then(response => {
                setDriveEmail(response?.data?.mail ?? "");
            });
        } else {
            setDriveEmail("");
        }
    }, [msAccessToken]);

    const handleCloseClick = () => {
        handleClose();
    };

    const handleDriveNameChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setDriveName(val ?? "");
    };

    const handleDriveLinkChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setDriveLink(val ?? "");
        setGoogleAuthToken("");
        setMSAccessToken("");
    };

    /** Returns true if the drive link looks like a valid URL for the selected provider */
    const isDriveLinkValid = () => {
        if (!driveLink) return false;
        if (driveType === DriveType.ms || driveType === DriveType.sharepoint) {
            try {
                const url = new URL(driveLink);
                return url.protocol === "https:" || url.protocol === "http:";
            } catch {
                return false;
            }
        }
        return true;
    };

    const isFileFilterValid = (filter: string): boolean => {
        if (!filter) return true;
        try {
            new RegExp(filter);
            return true;
        } catch {
            return false;
        }
    };

    const handleConnectClick = () => {
        if (!user?.companyId || !user?.currentKnowledgeBaseId || !driveName || !driveLink) return;
        if (!isDriveLinkValid()) {
            setErrorMessage(
                "Please enter a valid SharePoint or OneDrive URL (must start with https://)."
            );
            setStep(LoaderStep.error);
            return;
        }
        if (fileFilter && !isFileFilterValid(fileFilter)) {
            setErrorMessage("Invalid file filter regular expression.");
            setStep(LoaderStep.error);
            return;
        }

        setStep(LoaderStep.loading);
        const params =
            driveType === DriveType.google
                ? {
                      name: ProviderType.googleDrive,
                      displayName: driveName,
                      googleDriveUrl: driveLink,
                      gat: googleAuthToken || undefined,
                      email: driveEmail || undefined,
                      fileFilter: fileFilter || undefined,
                      citationLiveLink: liveSheetLinks || undefined,
                      knowledgeBaseId: user.currentKnowledgeBaseId
                  }
                : {
                      name: ProviderType.oneDrive,
                      displayName: driveName,
                      oneDriveUrl: driveLink,
                      oneAccessToken: msAccessToken,
                      email: driveEmail,
                      fileFilter: fileFilter || undefined,
                      citationLiveLink: liveSheetLinks || undefined,
                      knowledgeBaseId: user.currentKnowledgeBaseId
                  };
        addContentItemProvider(user.companyId, params)
            .then(() => {
                setStep(LoaderStep.complete);
                handleSuccess();
            })
            .catch(error => {
                switch (error.code) {
                    case "ERR_NETWORK":
                        setErrorMessage("Network error.");
                        break;
                    case "ERR_BAD_REQUEST":
                        setErrorMessage(`There was a problem connecting Google Drive "${driveName}".
                        Check its permissions for external access.`);
                        break;
                    default:
                        setErrorMessage("Something went wrong. Please try again later.");
                }
                setStep(LoaderStep.error);
            });
    };

    const handlePickerOpen = (onedrive8 = false) => {
        switch (driveType) {
            case DriveType.google:
                // Opens Google Picker via central auth domain popup
                openGooglePicker();
                break;
            case DriveType.ms:
                // Opens OneDrive Picker via central auth domain popup
                // Use SDK 8.0 if onedrive8 is true, otherwise use legacy picker
                onedrive8 ? openOneDrivePicker8() : openOneDrivePicker();
                break;
            case DriveType.sharepoint:
                // Opens SharePoint Picker via central auth domain popup
                openSharePointPicker();
                break;
        }
    };

    if (driveType === DriveType.web) {
        return (
            <LinkWebSiteView
                handleClose={handleClose}
                handleSuccess={handleSuccess}
                initialName={driveName}
                sourceTypes={{
                    list: Object.values(DriveType),
                    selected: DriveType.web,
                    onSelect: (type: string) => setDriveType(type as DriveType)
                }}
            />
        );
    }

    return (
        <div className="link-google-drive">
            {step === LoaderStep.start && (
                <>
                    <div className="link-google-drive-title">
                        <SvgIcon
                            className="link-google-drive-title__icon"
                            component={DriveIcon}
                            inheritViewBox
                        />
                        <span
                            className="link-google-drive-title__text"
                            role="heading"
                            aria-level={1}
                        >
                            Add Source
                        </span>
                    </div>
                    <div className="link-google-drive-description">
                        <span className="link-google-drive-description__text">
                            You can add a new source: a drive (&quot;Google Drive&quot;, &quot;One
                            Drive&quot; or &quot;SharePoint&quot;) or a public &quot;Web site&quot;
                        </span>
                    </div>
                    <div className="link-google-drive-data">
                        <input
                            type="text"
                            placeholder="Drive Name"
                            aria-label="Drive Name"
                            onChange={handleDriveNameChange}
                            value={driveName}
                        />
                        <SelectionFilter
                            className="link-google-drive-data-selection"
                            list={Object.values(DriveType)}
                            selectedItem={driveType}
                            setSelectedItem={(type: string) => {
                                if (type !== driveType) {
                                    setGoogleAuthToken("");
                                    setMSAccessToken("");
                                    setDriveLink("");
                                    setDriveType(type as DriveType);
                                }
                            }}
                            placeholder="Select Source Drive Type"
                            dropdownMenuType={DropdownMenuType.wider}
                            rightPosition={true}
                            minWidth={534}
                        />
                        <div className="link-google-drive-data-container">
                            <input
                                className={
                                    googleAuthToken || msAccessToken ? "input--placeholder" : ""
                                }
                                type="text"
                                placeholder={
                                    driveType === DriveType.ms
                                        ? "Paste One Drive URL or use picker..."
                                        : driveType === DriveType.sharepoint
                                          ? "Paste SharePoint URL or use picker..."
                                          : "Copy Drive URL or..."
                                }
                                onChange={handleDriveLinkChange}
                                value={driveLink}
                                disabled={!driveType}
                                aria-label="Drive URL"
                                aria-invalid={
                                    (driveType === DriveType.ms ||
                                        driveType === DriveType.sharepoint) &&
                                    driveLink &&
                                    !isDriveLinkValid()
                                        ? true
                                        : undefined
                                }
                                aria-describedby={
                                    (driveType === DriveType.ms ||
                                        driveType === DriveType.sharepoint) &&
                                    driveLink &&
                                    !isDriveLinkValid()
                                        ? `${fieldId}-drive-link-error`
                                        : undefined
                                }
                            />
                            {/* Show Sign In button when valid MS URL is pasted manually without a token */}
                            {(driveType === DriveType.ms || driveType === DriveType.sharepoint) &&
                            driveLink &&
                            !msAccessToken ? (
                                <Button
                                    variant="text"
                                    className="primary-button"
                                    onClick={openMSAuth}
                                    disabled={!isDriveLinkValid()}
                                >
                                    Sign In
                                </Button>
                            ) : (
                                <Button
                                    variant="text"
                                    className="primary-button"
                                    onClick={() => {
                                        handlePickerOpen();
                                    }}
                                    disabled={!driveType}
                                >
                                    Pick Drive Manually
                                </Button>
                            )}
                        </div>
                        {/* Show URL validation hint for MS providers */}
                        {(driveType === DriveType.ms || driveType === DriveType.sharepoint) &&
                            driveLink &&
                            !isDriveLinkValid() && (
                                <span
                                    className="link-google-drive-data__url-hint"
                                    id={`${fieldId}-drive-link-error`}
                                >
                                    Enter a valid URL starting with https://
                                </span>
                            )}
                        {/* Show auth status for MS providers with manually pasted URL */}
                        {(driveType === DriveType.ms || driveType === DriveType.sharepoint) &&
                            driveLink &&
                            msAccessToken && (
                                <span className="link-google-drive-data__auth-status">
                                    Signed in{driveEmail ? ` as ${driveEmail}` : ""}
                                </span>
                            )}
                        {!!driveType && (
                            <div className="link-google-drive-live-link">
                                <input
                                    type="checkbox"
                                    id={`${fieldId}-live-link`}
                                    checked={liveSheetLinks}
                                    onChange={e => setLiveSheetLinks(e.target.checked)}
                                />
                                <label htmlFor={`${fieldId}-live-link`}>
                                    Link spreadsheet citations to the live file
                                </label>
                                <span className="link-google-drive-advanced__hint">
                                    Spreadsheet citations also offer opening the live file (Google
                                    Sheets or Excel for the web) at the cited rows. Viewers need
                                    access to the documents. Can be changed later under the
                                    source&apos;s Visibility settings.
                                </span>
                            </div>
                        )}
                        <div className="link-google-drive-advanced">
                            <button
                                type="button"
                                className="link-google-drive-advanced__toggle"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                            >
                                {showAdvanced ? "\u25BE" : "\u25B8"} Advanced Settings
                            </button>
                            {showAdvanced && (
                                <div className="link-google-drive-advanced__content">
                                    <label
                                        className="link-google-drive-advanced__label"
                                        htmlFor={`${fieldId}-file-filter`}
                                    >
                                        File Filter (regex)
                                    </label>
                                    <input
                                        type="text"
                                        id={`${fieldId}-file-filter`}
                                        placeholder="e.g. \.pdf$|\.docx$"
                                        value={fileFilter}
                                        onChange={e => setFileFilter(e.target.value)}
                                        aria-invalid={
                                            fileFilter && !isFileFilterValid(fileFilter)
                                                ? true
                                                : undefined
                                        }
                                        aria-describedby={
                                            fileFilter && !isFileFilterValid(fileFilter)
                                                ? `${fieldId}-file-filter-error`
                                                : undefined
                                        }
                                    />
                                    {fileFilter && !isFileFilterValid(fileFilter) && (
                                        <span
                                            className="link-google-drive-data__url-hint"
                                            id={`${fieldId}-file-filter-error`}
                                        >
                                            Invalid regular expression
                                        </span>
                                    )}
                                    <span className="link-google-drive-advanced__hint">
                                        Only files whose path matches this pattern will be
                                        processed. Matched against: folder_path/file_name
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
            {step === LoaderStep.loading && (
                <div className="link-source-loading">
                    <div className="link-source-loading-title">
                        <Loader size={40} borderWidth={5} />
                        <span className="link-source-loading-title__text">
                            Connection in Progress
                        </span>
                    </div>
                    <span className="link-source-loading-description">{loadingDescription}</span>
                </div>
            )}
            {step === LoaderStep.complete && (
                <div className="link-source-complete">
                    <div className="link-source-complete-title">
                        <SvgIcon
                            className="link-source-complete-title__icon"
                            component={CloudIcon}
                            inheritViewBox
                        />
                        <span className="link-source-complete-title__text">
                            External Source Connected
                        </span>
                    </div>
                    <span className="link-source-complete-description">{completeDescription}</span>
                </div>
            )}
            {step === LoaderStep.error && (
                <div className="link-source-error">
                    <div className="link-source-error-title">
                        <SvgIcon
                            className="link-source-error-title__icon"
                            component={CloudErrorIcon}
                            inheritViewBox
                        />
                        <span className="link-source-error-title__text">Connection Error</span>
                    </div>
                    <span className="link-source-error-description">{errorMessage}</span>
                </div>
            )}
            <div className="link-google-drive-action">
                {(step === LoaderStep.start || step === LoaderStep.loading) && (
                    <>
                        <Button
                            className="thirt-button"
                            variant="text"
                            onClick={handleCloseClick}
                            disabled={step === LoaderStep.loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="primary-button second-button"
                            variant="text"
                            onClick={handleConnectClick}
                            disabled={
                                !driveLink ||
                                !driveName ||
                                step === LoaderStep.loading ||
                                ((driveType === DriveType.ms ||
                                    driveType === DriveType.sharepoint) &&
                                    !msAccessToken)
                            }
                        >
                            {step === LoaderStep.start ? "Add Source" : "Please wait"}
                        </Button>
                    </>
                )}
                {(step === LoaderStep.complete || step === LoaderStep.error) && (
                    <Button variant="text" className="primary-button" onClick={handleCloseClick}>
                        Close
                    </Button>
                )}
            </div>
        </div>
    );
};

export default LinkGoogleDriveView;
